import React, { useState, useMemo, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import dayjs from 'dayjs';
import { toast } from "react-toastify";
import { FaMagic, FaRedo, FaCog, FaSpinner, FaExpand, FaCopy, FaStream, FaCode, FaBan, FaFilePdf } from "react-icons/fa";
import { FiCpu, FiCheckCircle, FiAlertTriangle, FiClock, FiSlash } from "react-icons/fi";
import CrmListPage from "components/app/crm/shared/CrmListPage";
import { Modal, Button, confirmAlert } from 'components/common';
import JsonTree from "components/common/JsonTree";
import ModelBadge from "components/common/ModelBadge";
import { getExtractions, retryExtraction, cancelExtraction } from "redux/document/extractionAdminSlice";
import ExtractionPromptConfigDrawer from "components/app/authorization/ExtractionPromptConfigDrawer";

// Status pill (mirrors ContractExtractionStatus chipMeta).
const STATUS_META = {
  completed:  { txt: "Completed", cls: "tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200", Icon: FiCheckCircle,  spin: false },
  failed:     { txt: "Failed",    cls: "tw-bg-red-50 tw-text-red-700 tw-border-red-200",             Icon: FiAlertTriangle, spin: false },
  processing: { txt: "Processing", cls: "tw-bg-blue-50 tw-text-blue-700 tw-border-blue-200",         Icon: FiCpu,           spin: true  },
  pending:    { txt: "Pending",   cls: "tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200",       Icon: FiClock,         spin: false },
  canceled:   { txt: "Canceled",  cls: "tw-bg-slate-100 tw-text-slate-600 tw-border-slate-200",      Icon: FiSlash,         spin: false },
};

const StatusCell = ({ value }) => {
  const m = STATUS_META[value];
  if (!m) return <span className="tw-text-slate-400">{value || "—"}</span>;
  const { txt, cls, Icon, spin } = m;
  return (
    <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-xl tw-text-[11px] tw-font-semibold tw-border ${cls}`}>
      <Icon size={12} className={spin ? "tw-animate-spin" : ""} /> {txt}
    </span>
  );
};
StatusCell.propTypes = { value: PropTypes.string };

const humanize = (v) => (v ? String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

// Normalize a large field to a readable string. json → pretty-printed (2 spaces).
const stringifyValue = (value, json) => {
  if (value === null || value === undefined || value === "") return "";
  if (json) {
    try { return JSON.stringify(typeof value === "string" ? JSON.parse(value) : value, null, 2); }
    catch { return String(value); }
  }
  return String(value);
};

const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Lightweight JSON syntax highlighter (no dependency). Operates on already-escaped
// text and only wraps tokens in <span> — safe from injection since <>& are neutralized.
const highlightJson = (json) =>
  escapeHtml(json).replace(
    /("(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?)|(\btrue\b|\bfalse\b)|(\bnull\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (m, str, bool, nul, num) => {
      if (str) return `<span class="${/:\s*$/.test(str) ? "tw-text-sky-300" : "tw-text-emerald-300"}">${str}</span>`;
      if (bool) return `<span class="tw-text-fuchsia-300">${bool}</span>`;
      if (nul) return `<span class="tw-text-slate-500">${nul}</span>`;
      return `<span class="tw-text-amber-300">${num}</span>`;
    }
  );

const VIEWER_STYLE = { fontSize: 12.5, lineHeight: "20px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", whiteSpace: "pre" };

// Read-only viewer: dark theme + colored JSON tokens + synced line-number gutter.
const CodeViewer = ({ text, json, height }) => {
  const contentRef = useRef(null);
  const gutterRef = useRef(null);
  const lineCount = useMemo(() => Math.max(text.split("\n").length, 1), [text]);
  const numbers = useMemo(() => Array.from({ length: lineCount }, (_, i) => i + 1).join("\n"), [lineCount]);
  const html = useMemo(() => (json ? highlightJson(text) : escapeHtml(text)), [text, json]);
  const syncScroll = () => { if (gutterRef.current && contentRef.current) gutterRef.current.scrollTop = contentRef.current.scrollTop; };

  return (
    <div className="tw-flex tw-rounded-sm tw-border tw-border-solid tw-border-slate-700 tw-overflow-hidden tw-bg-slate-900" style={{ height }}>
      <pre ref={gutterRef} aria-hidden="true" className="tw-m-0 tw-py-3 tw-px-2 tw-text-right tw-select-none tw-overflow-hidden tw-text-slate-500 tw-bg-slate-800" style={{ ...VIEWER_STYLE, minWidth: 46 }}>
        {numbers}
      </pre>
      <pre ref={contentRef} onScroll={syncScroll} className="tw-m-0 tw-py-3 tw-px-3 tw-flex-1 tw-overflow-auto tw-text-slate-100" style={VIEWER_STYLE} dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};
CodeViewer.propTypes = { text: PropTypes.string, json: PropTypes.bool, height: PropTypes.string };

// Grid cell for big text/JSON (raw_text, llm_result): shows a short preview and an
// Expand button that opens a fullscreen viewer with copy — no cramming into the cell.
const ViewDataCell = ({ value, label, json }) => {
  const [open, setOpen] = useState(false);
  const [treeView, setTreeView] = useState(json);
  const text = useMemo(() => stringifyValue(value, json), [value, json]);
  // Parsed object for the collapsible tree (undefined when not valid JSON).
  const parsed = useMemo(() => {
    if (!json || !text) return undefined;
    try { return typeof value === "string" ? JSON.parse(value) : value; } catch { return undefined; }
  }, [json, value, text]);
  const canTree = parsed !== undefined && parsed !== null && typeof parsed === "object";
  const showTree = canTree && treeView;

  if (!text) return <span className="tw-text-slate-300">—</span>;

  const preview = text.replace(/\s+/g, " ").trim().slice(0, 60);
  const copy = () => {
    try { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="View full content"
        className="tw-inline-flex tw-items-center tw-gap-1 tw-max-w-full tw-text-xs tw-text-blue-600 hover:tw-underline tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0"
      >
        <FaExpand size={10} className="tw-shrink-0" />
        <span className="tw-truncate">{preview || "View"}</span>
      </button>
      <Modal
        show={open}
        onHide={() => setOpen(false)}
        title={label}
        fullscreen
        footer={<Button size="sm" variant="primary" onClick={() => setOpen(false)}>Close</Button>}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <span className="tw-text-xs tw-text-slate-400">{json ? "JSON" : "Text"} · {text.length.toLocaleString()} chars</span>
          <div className="tw-flex tw-items-center tw-gap-3">
            {canTree && (
              <button type="button" onClick={() => setTreeView((t) => !t)} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-blue-600 hover:tw-underline tw-bg-transparent tw-border-0 tw-cursor-pointer">
                {showTree ? <><FaCode size={11} /> Raw</> : <><FaStream size={11} /> Tree</>}
              </button>
            )}
            <button type="button" onClick={copy} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-600 hover:tw-text-slate-900 tw-bg-transparent tw-border-0 tw-cursor-pointer">
              <FaCopy size={11} /> Copy
            </button>
          </div>
        </div>
        {showTree
          ? <JsonTree data={parsed} height="max(320px, calc(100dvh - 190px))" />
          : <CodeViewer text={text} json={json} height="max(320px, calc(100dvh - 190px))" />}
      </Modal>
    </>
  );
};
ViewDataCell.propTypes = { value: PropTypes.any, label: PropTypes.string, json: PropTypes.bool };

const RetryButton = ({ row, onRetry }) => {
  const [busy, setBusy] = useState(false);
  const disabled = busy || row?.status === "processing";
  const click = () => {
    if (disabled) return;
    confirmAlert({
      title: "Re-run extraction?",
      message: `Re-queue the AI extraction for "${row?.original_filename || "this file"}"? It will reprocess with the current prompt/model config.`,
      confirmText: "Retry",
      onConfirm: async () => {
        setBusy(true);
        try { await onRetry(row.id); } finally { setBusy(false); }
      },
    });
  };
  return (
    <button
      type="button"
      onClick={click}
      disabled={disabled}
      title={row?.status === "processing" ? "Currently processing" : "Re-run extraction"}
      className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-sm tw-border tw-border-primary-300 tw-bg-primary-50 tw-text-primary-700 hover:tw-bg-primary-100 tw-text-xs tw-font-medium tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
    >
      {busy ? <FaSpinner size={11} className="tw-animate-spin" /> : <FaRedo size={11} />} Retry
    </button>
  );
};
RetryButton.propTypes = { row: PropTypes.object, onRetry: PropTypes.func };

// Only a queued/running extraction can be stopped — mirrors Extraction#cancellable?
const CancelButton = ({ row, onCancel }) => {
  const [busy, setBusy] = useState(false);
  if (!["pending", "processing"].includes(row?.status)) return null;

  const click = () => {
    if (busy) return;
    confirmAlert({
      title: "Cancel extraction?",
      message: `Stop the AI extraction for "${row?.original_filename || "this file"}"? A request already sent to the AI may still finish, but no further pages will be processed. You can retry it later.`,
      confirmText: "Cancel run",
      onConfirm: async () => {
        setBusy(true);
        try { await onCancel(row.id); } finally { setBusy(false); }
      },
    });
  };

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      title="Cancel this extraction"
      className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 hover:tw-text-red-600 tw-text-xs tw-font-medium tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
    >
      {busy ? <FaSpinner size={11} className="tw-animate-spin" /> : <FaBan size={11} />} Cancel
    </button>
  );
};
CancelButton.propTypes = { row: PropTypes.object, onCancel: PropTypes.func };

const SELECTS = [
  { key: "status", paramKey: "status_eq", default: "", options: [
    { value: "", label: "All statuses" },
    { value: "failed", label: "Failed" },
    { value: "processing", label: "Processing" },
    { value: "pending", label: "Pending" },
    { value: "completed", label: "Completed" },
    { value: "canceled", label: "Canceled" },
  ] },
  { key: "document_type", paramKey: "document_type_eq", default: "", options: [
    { value: "", label: "All document types" },
    { value: "forecast_material", label: "Forecast material" },
    { value: "released_estimate", label: "Released estimate" },
    { value: "price_matrix", label: "Price matrix" },
    { value: "contract", label: "Contract" },
  ] },
  { key: "group_by_file", paramKey: "group_by_file", default: "", options: [
    { value: "", label: "Grouped by file" },
    { value: "false", label: "All attempts" },
  ] },
];

// Created defaults to this month so the list opens on a focused, recent window;
// Updated is an optional range the user can add.
const DATE_FILTERS = [
  { key: "created_at", label: "Created", paramKeys: ["created_at_gteq", "created_at_lteq"], defaultThisMonth: true },
  { key: "updated_at", label: "Updated", paramKeys: ["updated_at_gteq", "updated_at_lteq"] },
];

const TABS = [{ key: "all", label: "All", Icon: FaMagic, thunk: getExtractions, dataKey: "allData" }];

const DocumentExtractionsPage = () => {
  const dispatch = useDispatch();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [showPromptConfig, setShowPromptConfig] = useState(false);

  const handleRetry = useCallback(async (id) => {
    try {
      await dispatch(retryExtraction(id)).unwrap();
      setReloadSignal((x) => x + 1);
    } catch (e) { /* error toast handled in slice */ }
  }, [dispatch]);

  const handleCancel = useCallback(async (id) => {
    try {
      await dispatch(cancelExtraction(id)).unwrap();
      setReloadSignal((x) => x + 1);
    } catch (e) { /* error toast handled in slice */ }
  }, [dispatch]);

  const columns = useMemo(() => [
    { headerName: "File", field: "original_filename", minWidth: 200, width: 260, flex: 0, pinned: 'left',
      cellRenderer: (p) => {
        const n = p.data?.versions_count;
        // document_url is a presigned S3 link, generated per request by the
        // serializer — open it in a new tab so the grid keeps its state.
        const url = p.data?.document_url;
        return (
          <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-max-w-full">
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title="Open the source document in a new tab"
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-min-w-0 tw-font-medium tw-text-blue-600 hover:tw-underline"
              >
                <FaFilePdf size={11} className="tw-shrink-0 tw-text-red-500" />
                <span className="tw-truncate">{p.value || "—"}</span>
              </a>
            ) : (
              <span className="tw-font-medium tw-text-slate-700 tw-truncate">{p.value || "—"}</span>
            )}
            {n > 1 && (
              <span title={`${n} extraction attempts for this file`} className="tw-shrink-0 tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-bg-slate-100 tw-text-slate-600 tw-border tw-border-slate-200">
                ×{n}
              </span>
            )}
          </span>
        );
      } },
    { headerName: "Type", field: "document_type", minWidth: 150, cellRenderer: (p) => humanize(p.value) },
    { headerName: "Status", field: "status", minWidth: 130, sortable: false, cellRenderer: StatusCell },
    { headerName: "Model", field: "llm_model", minWidth: 170, cellRenderer: (p) => <ModelBadge model={p.value} /> },
    { headerName: "Tokens", field: "llm_tokens_used", minWidth: 110, cellRenderer: (p) => (p.value ? Number(p.value).toLocaleString() : "—") },
    { headerName: "Pages", field: "processed_pages", minWidth: 110, sortable: false,
      cellRenderer: (p) => {
        const total = p.data?.total_pages;
        return <span className="tw-tabular-nums">{p.value ?? 0}{total != null ? ` / ${total}` : ""}</span>;
      } },
    { headerName: "Source type", field: "source_type", minWidth: 130, sortable: false, cellRenderer: (p) => humanize(p.value) },
    { headerName: "Source ID", field: "source_id", minWidth: 100, sortable: false,
      cellRenderer: (p) => (p.value ? <span className="tw-tabular-nums">{p.value}</span> : <span className="tw-text-slate-300">—</span>) },
    { headerName: "Raw text", field: "raw_text", minWidth: 180, sortable: false,
      cellRenderer: (p) => <ViewDataCell value={p.value} label="Raw text" /> },
    { headerName: "LLM result", field: "llm_result", minWidth: 180, sortable: false,
      cellRenderer: (p) => <ViewDataCell value={p.value} label="LLM result" json /> },
    { headerName: "Uploaded by", field: "uploaded_by", minWidth: 140 },
    { headerName: "Created", field: "created_at", minWidth: 160, cellRenderer: (p) => (p.value ? dayjs(p.value).format("DD-MMM-YYYY HH:mm") : "—") },
    { headerName: "Updated", field: "updated_at", minWidth: 160, cellRenderer: (p) => (p.value ? dayjs(p.value).format("DD-MMM-YYYY HH:mm") : "—") },
    { headerName: "Error", field: "error_message", minWidth: 260, flex: 1, sortable: false,
      cellRenderer: (p) => (p.value ? <span className="tw-text-red-600 tw-text-xs" title={p.value}>{String(p.value).slice(0, 140)}</span> : <span className="tw-text-slate-300">—</span>) },
    { headerName: "Actions", field: "id", minWidth: 190, sortable: false, pinned: 'right',
      cellRenderer: (p) => (
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <RetryButton row={p.data} onRetry={handleRetry} />
          <CancelButton row={p.data} onCancel={handleCancel} />
        </div>
      ) },
  ], [handleRetry, handleCancel]);

  const headerExtra = (
    <button
      type="button"
      onClick={() => setShowPromptConfig(true)}
      className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50 tw-text-sm tw-font-medium tw-cursor-pointer"
    >
      <FaCog size={12} /> Prompt config
    </button>
  );

  return (
    <>
      <CrmListPage
        title="AI Document Extractions"
        subtitle="Track document AI extractions across all users; re-run failed ones. Grouped by file."
        Icon={FaMagic}
        reducerSelector={(s) => s.admin_document_extraction}
        tabs={TABS}
        columns={columns}
        selects={SELECTS}
        dateFilters={DATE_FILTERS}
        searchParamKey="original_filename_or_error_message_cont"
        searchPlaceholder="File name or error..."
        headerExtra={headerExtra}
        reloadSignal={reloadSignal}
      />

      <ExtractionPromptConfigDrawer show={showPromptConfig} onClose={() => setShowPromptConfig(false)} />
    </>
  );
};

export default DocumentExtractionsPage;
